import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemConfigTab = () => {
  const [systemConfig, setSystemConfig] = useState({
    nominalCodes: [
      { code: '4000', description: 'Sales - General', vatRate: 20, isDefault: true },
      { code: '4001', description: 'Sales - Services', vatRate: 20, isDefault: false },
      { code: '4002', description: 'Sales - Products', vatRate: 20, isDefault: false },
      { code: '4003', description: 'Sales - Zero Rated', vatRate: 0, isDefault: false },
      { code: '4004', description: 'Sales - Exempt', vatRate: 0, isDefault: false }
    ],
    taxCodes: [
      { code: 'T1', description: 'Standard Rate VAT (20%)', rate: 20, isActive: true },
      { code: 'T2', description: 'Reduced Rate VAT (5%)', rate: 5, isActive: true },
      { code: 'T0', description: 'Zero Rate VAT (0%)', rate: 0, isActive: true },
      { code: 'T9', description: 'Exempt from VAT', rate: 0, isActive: true }
    ],
    exchangeRates: {
      baseCurrency: 'GBP',
      autoUpdate: true,
      updateFrequency: 'daily',
      fallbackRates: {
        'EUR': 1.17,
        'USD': 1.27
      }
    },
    processingSettings: {
      maxBatchSize: 50,
      timeoutSeconds: 300,
      retryAttempts: 3,
      confidenceThreshold: 0.7,
      enableFallbackOCR: true,
      autoReview: false
    },
    securitySettings: {
      sessionTimeout: 480,
      passwordMinLength: 8,
      requireSpecialChars: true,
      auditRetentionDays: 365,
      enableTwoFactor: false
    },
    backupSettings: {
      autoBackup: true,
      backupFrequency: 'daily',
      retentionDays: 30,
      includeFiles: true
    }
  });

  const [newNominalCode, setNewNominalCode] = useState({
    code: '',
    description: '',
    vatRate: 20,
    isDefault: false
  });

  const [newTaxCode, setNewTaxCode] = useState({
    code: '',
    description: '',
    rate: 20,
    isActive: true
  });

  const updateFrequencyOptions = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'manual', label: 'Manual Only' }
  ];

  const backupFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleConfigChange = (section, field, value) => {
    setSystemConfig(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
  };

  const handleAddNominalCode = () => {
    if (newNominalCode?.code && newNominalCode?.description) {
      setSystemConfig(prev => ({
        ...prev,
        nominalCodes: [...prev?.nominalCodes, { ...newNominalCode }]
      }));
      setNewNominalCode({
        code: '',
        description: '',
        vatRate: 20,
        isDefault: false
      });
    }
  };

  const handleRemoveNominalCode = (index) => {
    setSystemConfig(prev => ({
      ...prev,
      nominalCodes: prev?.nominalCodes?.filter((_, i) => i !== index)
    }));
  };

  const handleAddTaxCode = () => {
    if (newTaxCode?.code && newTaxCode?.description) {
      setSystemConfig(prev => ({
        ...prev,
        taxCodes: [...prev?.taxCodes, { ...newTaxCode }]
      }));
      setNewTaxCode({
        code: '',
        description: '',
        rate: 20,
        isActive: true
      });
    }
  };

  const handleRemoveTaxCode = (index) => {
    setSystemConfig(prev => ({
      ...prev,
      taxCodes: prev?.taxCodes?.filter((_, i) => i !== index)
    }));
  };

  const handleSaveConfig = () => {
    console.log('Saving system configuration:', systemConfig);
  };

  const handleTestConnection = () => {
    console.log('Testing system connections...');
  };

  return (
    <div className="space-y-8">
      {/* Nominal Codes Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Hash" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Nominal Codes</h3>
              <p className="text-sm text-muted-foreground">Configure Sage 50 nominal account codes</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Import from Sage
          </Button>
        </div>

        {/* Add New Nominal Code */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg mb-4">
          <Input
            label="Code"
            value={newNominalCode?.code}
            onChange={(e) => setNewNominalCode(prev => ({ ...prev, code: e?.target?.value }))}
            placeholder="4000"
          />
          <Input
            label="Description"
            value={newNominalCode?.description}
            onChange={(e) => setNewNominalCode(prev => ({ ...prev, description: e?.target?.value }))}
            placeholder="Account description"
          />
          <Input
            type="number"
            label="VAT Rate (%)"
            value={newNominalCode?.vatRate}
            onChange={(e) => setNewNominalCode(prev => ({ ...prev, vatRate: parseInt(e?.target?.value) }))}
            min={0}
            max={100}
          />
          <div className="flex items-end">
            <Button onClick={handleAddNominalCode} iconName="Plus" iconPosition="left">
              Add Code
            </Button>
          </div>
        </div>

        {/* Nominal Codes List */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-foreground">Code</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Description</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">VAT Rate</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Default</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {systemConfig?.nominalCodes?.map((code, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4 font-mono text-foreground">{code?.code}</td>
                  <td className="py-3 px-4 text-foreground">{code?.description}</td>
                  <td className="py-3 px-4 text-foreground">{code?.vatRate}%</td>
                  <td className="py-3 px-4">
                    {code?.isDefault && (
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveNominalCode(index)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Tax Codes Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Percent" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Tax Codes</h3>
            <p className="text-sm text-muted-foreground">Configure VAT tax codes for different rates</p>
          </div>
        </div>

        {/* Add New Tax Code */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg mb-4">
          <Input
            label="Tax Code"
            value={newTaxCode?.code}
            onChange={(e) => setNewTaxCode(prev => ({ ...prev, code: e?.target?.value }))}
            placeholder="T1"
          />
          <Input
            label="Description"
            value={newTaxCode?.description}
            onChange={(e) => setNewTaxCode(prev => ({ ...prev, description: e?.target?.value }))}
            placeholder="Tax description"
          />
          <Input
            type="number"
            label="Rate (%)"
            value={newTaxCode?.rate}
            onChange={(e) => setNewTaxCode(prev => ({ ...prev, rate: parseInt(e?.target?.value) }))}
            min={0}
            max={100}
          />
          <div className="flex items-end">
            <Button onClick={handleAddTaxCode} iconName="Plus" iconPosition="left">
              Add Tax Code
            </Button>
          </div>
        </div>

        {/* Tax Codes List */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-foreground">Code</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Description</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Rate</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Status</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {systemConfig?.taxCodes?.map((tax, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4 font-mono text-foreground">{tax?.code}</td>
                  <td className="py-3 px-4 text-foreground">{tax?.description}</td>
                  <td className="py-3 px-4 text-foreground">{tax?.rate}%</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tax?.isActive 
                        ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}>
                      {tax?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTaxCode(index)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Processing Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Cpu" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Processing Settings</h3>
            <p className="text-sm text-muted-foreground">Configure OCR processing parameters</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              type="number"
              label="Max Batch Size"
              value={systemConfig?.processingSettings?.maxBatchSize}
              onChange={(e) => handleConfigChange('processingSettings', 'maxBatchSize', parseInt(e?.target?.value))}
              description="Maximum number of invoices per batch"
              min={1}
              max={100}
            />

            <Input
              type="number"
              label="Timeout (seconds)"
              value={systemConfig?.processingSettings?.timeoutSeconds}
              onChange={(e) => handleConfigChange('processingSettings', 'timeoutSeconds', parseInt(e?.target?.value))}
              description="Processing timeout per invoice"
              min={30}
              max={600}
            />

            <Input
              type="number"
              label="Retry Attempts"
              value={systemConfig?.processingSettings?.retryAttempts}
              onChange={(e) => handleConfigChange('processingSettings', 'retryAttempts', parseInt(e?.target?.value))}
              description="Number of retry attempts on failure"
              min={0}
              max={5}
            />
          </div>

          <div className="space-y-4">
            <Input
              type="number"
              label="Confidence Threshold"
              value={systemConfig?.processingSettings?.confidenceThreshold}
              onChange={(e) => handleConfigChange('processingSettings', 'confidenceThreshold', parseFloat(e?.target?.value))}
              description="Minimum confidence for auto-approval"
              min={0}
              max={1}
              step={0.1}
            />

            <Checkbox
              label="Enable Fallback OCR"
              description="Use Mistral when Gemini fails"
              checked={systemConfig?.processingSettings?.enableFallbackOCR}
              onChange={(e) => handleConfigChange('processingSettings', 'enableFallbackOCR', e?.target?.checked)}
            />

            <Checkbox
              label="Auto Review"
              description="Automatically mark high-confidence invoices as reviewed"
              checked={systemConfig?.processingSettings?.autoReview}
              onChange={(e) => handleConfigChange('processingSettings', 'autoReview', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Exchange Rates */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Exchange Rates</h3>
            <p className="text-sm text-muted-foreground">Configure currency exchange rate handling</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Base Currency
              </label>
              <div className="text-sm text-muted-foreground">
                GBP (British Pound) - Primary currency for calculations
              </div>
            </div>

            <Checkbox
              label="Auto Update Rates"
              description="Automatically fetch latest exchange rates"
              checked={systemConfig?.exchangeRates?.autoUpdate}
              onChange={(e) => handleConfigChange('exchangeRates', 'autoUpdate', e?.target?.checked)}
            />

            <Select
              label="Update Frequency"
              options={updateFrequencyOptions}
              value={systemConfig?.exchangeRates?.updateFrequency}
              onChange={(value) => handleConfigChange('exchangeRates', 'updateFrequency', value)}
              disabled={!systemConfig?.exchangeRates?.autoUpdate}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Fallback Rates</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-foreground w-12">EUR:</span>
                <Input
                  type="number"
                  value={systemConfig?.exchangeRates?.fallbackRates?.EUR}
                  onChange={(e) => handleConfigChange('exchangeRates', 'fallbackRates', {
                    ...systemConfig?.exchangeRates?.fallbackRates,
                    EUR: parseFloat(e?.target?.value)
                  })}
                  step={0.01}
                  className="flex-1"
                />
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-foreground w-12">USD:</span>
                <Input
                  type="number"
                  value={systemConfig?.exchangeRates?.fallbackRates?.USD}
                  onChange={(e) => handleConfigChange('exchangeRates', 'fallbackRates', {
                    ...systemConfig?.exchangeRates?.fallbackRates,
                    USD: parseFloat(e?.target?.value)
                  })}
                  step={0.01}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Security Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Security Settings</h3>
            <p className="text-sm text-muted-foreground">Configure security and audit policies</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              type="number"
              label="Session Timeout (minutes)"
              value={systemConfig?.securitySettings?.sessionTimeout}
              onChange={(e) => handleConfigChange('securitySettings', 'sessionTimeout', parseInt(e?.target?.value))}
              description="Automatic logout after inactivity"
              min={15}
              max={1440}
            />

            <Input
              type="number"
              label="Password Minimum Length"
              value={systemConfig?.securitySettings?.passwordMinLength}
              onChange={(e) => handleConfigChange('securitySettings', 'passwordMinLength', parseInt(e?.target?.value))}
              min={6}
              max={32}
            />

            <Checkbox
              label="Require Special Characters"
              description="Passwords must contain special characters"
              checked={systemConfig?.securitySettings?.requireSpecialChars}
              onChange={(e) => handleConfigChange('securitySettings', 'requireSpecialChars', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <Input
              type="number"
              label="Audit Retention (days)"
              value={systemConfig?.securitySettings?.auditRetentionDays}
              onChange={(e) => handleConfigChange('securitySettings', 'auditRetentionDays', parseInt(e?.target?.value))}
              description="How long to keep audit logs"
              min={30}
              max={2555}
            />

            <Checkbox
              label="Enable Two-Factor Authentication"
              description="Require 2FA for all users"
              checked={systemConfig?.securitySettings?.enableTwoFactor}
              onChange={(e) => handleConfigChange('securitySettings', 'enableTwoFactor', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button 
          variant="outline" 
          onClick={handleTestConnection}
          iconName="TestTube"
          iconPosition="left"
        >
          Test Configuration
        </Button>
        
        <Button 
          onClick={handleSaveConfig}
          iconName="Save"
          iconPosition="left"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default SystemConfigTab;